import { useMemo } from 'react'
import { Count } from '@/types/count'
import { Document, DocumentForRegister } from '@/types/documents'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGet, { UseGetOptions } from '../core-hooks/useGet'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import {
  deleteCall,
  getDetail,
  getList,
  updateDetail,
  updateList,
} from '../request-wrappers'

const service = buildService<Document>({
  pathCategory: 'documents',
})

export function useGetDocument({
  id,
  ...options
}: UseGetDetailOptions<Document>) {
  const query = useGetDetail<Document>({
    id,
    queryKey: queryKeys.documents.detail({ id, filters: options.filters }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    document: query.data,
    ...query,
  }
}

export function useCreateDocument(
  options: UseCreateDetailOptions<Document> = {},
) {
  const mutation = useCreateDetail<Document>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createDocument: mutation.mutate,
    createDocumentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateDocument(
  options: UseUpdateDetailOptions<Document> = {},
) {
  const mutation = useUpdateDetail<Document>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateDocument: mutation.mutate,
    updateDocumentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocument(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<Document>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteDocument: mutation.mutate,
    deleteDocumentAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetDocuments({
  filters,
  ...options
}: UseGetListOptions<Document> = {}) {
  const updatedFilters = useMemo(
    () => ({
      'api-consistency': 'weak',
      ...filters, // Allow api-consistency to be overridden
    }),
    [filters],
  )
  const query = useGetList<Document>({
    filters: updatedFilters,
    queryKey: queryKeys.documents.list({ filters: updatedFilters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    documents: query.allData,
    ...query,
  }
}

export function useUpdateDocuments(
  options: UseUpdateListOptions<Document> = {},
) {
  const mutation = useUpdateList<Document>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateDocuments: mutation.mutate,
    updateDocumentsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteDocuments({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<Document>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteDocuments: mutation.mutate,
    deleteDocumentsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Register Documents
 * Used to create or update a list of documents.
 */
export type DocWithOptionalProjectId = Omit<Document, 'project_id'> & {
  project_id?: string
}
export function useRegisterDocuments(
  options: UseUpdateListOptions<
    DocumentForRegister,
    DocWithOptionalProjectId
  > = {},
) {
  const mutation = useUpdateList<DocumentForRegister, DocWithOptionalProjectId>(
    {
      serviceFn: (serviceFnOptions) => {
        return updateList<DocumentForRegister, DocWithOptionalProjectId>({
          url: '/v2/pd-workflow/documents/register',
          ...serviceFnOptions,
        })
      },
      ...options,
    },
  )

  return {
    registerDocuments: mutation.mutate,
    registerDocumentsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Complete Document Registration
 * Used to assign a document to a project.
 */
export type CompleteRegisterDocBody = {
  id: string
  project_id: string
}
export function useCompleteDocumentRegistration(
  options: UseUpdateListOptions<CompleteRegisterDocBody> = {},
) {
  const mutation = useUpdateList<CompleteRegisterDocBody>({
    serviceFn: (serviceFnOptions) => {
      return updateList<CompleteRegisterDocBody>({
        url: '/v2/pd-workflow/documents/register/complete',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    completeRegistration: mutation.mutate,
    completeRegistrationAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Run Rules on a Document
 * Used to run rules on a document.
 */
export function useRunRules({
  workflowId,
  ...options
}: UseUpdateDetailOptions<{ id: string }> & { workflowId: string }) {
  const mutation = useUpdateDetail<{ id: string }>({
    serviceFn: ({ item, ...serviceFnOptions }) => {
      return updateDetail<{ id: string }>({
        url: `/v2/pd/documents/${item.id}/run_rules`,
        item: {} as { id: string }, // No body needed for this endpoint
        ...serviceFnOptions,
        filters: {
          ...serviceFnOptions.filters,
          workflow_id: workflowId,
        },
      })
    },
    ...options,
  })

  return {
    runRules: mutation.mutate,
    runRulesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Clear Document Chips
 * Used to clear chips on a document.
 */
export function useClearDocumentChips(
  options: UseUpdateDetailOptions<{ id: string }> = {},
) {
  const mutation = useUpdateDetail<{ id: string }>({
    serviceFn: ({ item, ...serviceFnOptions }) => {
      return updateDetail<{ id: string }>({
        url: `/v2/pd/documents/${item.id}/clear_chips`,
        item: {} as { id: string }, // No body needed for this endpoint
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    clearDocumentChips: mutation.mutate,
    clearDocumentChipsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Archive Documents
 * Used to archive a list of documents.
 */
export function useArchiveDocuments(options: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<Document>({
    serviceFn: ({ ids, filters, axiosOptions }) => {
      return deleteCall({
        url: '/v2/pd/documents/archive',
        filters: {
          id__in: ids.join(','),
          ...filters,
        },
        axiosOptions,
      })
    },
    ...options,
  })

  return {
    archiveDocuments: mutation.mutate,
    archiveDocumentsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get Documents Count
 * Used to get the count of documents.
 */
export function useGetDocumentsCount({
  filters,
  ...options
}: UseGetListOptions<Count> = {}) {
  const updatedFilters = useMemo(
    () => ({
      'api-consistency': 'weak',
      ...filters, // Allow api-consistency to be overridden
    }),
    [filters],
  )
  const query = useGetList<Count>({
    filters: updatedFilters,
    queryKey: queryKeys.documents.list({ filters: updatedFilters }, ['count']),
    serviceFn: (serviceFnOptions) => {
      return getList<Count>({
        url: '/v2/pd/documents/~count',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    documentsCount: query.data?.pages[0].count || 0,
    groupedCounts: query.allData,
    ...query,
  }
}

/**
 * MARK: Action Hook: Get if Documents Exist
 * Used to check if documents exist based on filters.
 */
type ExistsResponse = {
  exists: boolean
}
export function useGetDocumentsExist({
  filters,
  ...options
}: UseGetOptions<ExistsResponse> = {}) {
  const updatedFilters = useMemo(
    () => ({
      'api-consistency': 'weak',
      ...filters, // Allow api-consistency to be overridden
    }),
    [filters],
  )
  const query = useGet<ExistsResponse>({
    filters: updatedFilters,
    queryKey: queryKeys.documents.list({ filters: updatedFilters }, ['exists']),
    serviceFn: (serviceFnOptions) => {
      return getDetail<ExistsResponse>({
        url: '/v2/pd/documents/~exists',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    exists: query.data?.exists || false,
    ...query,
  }
}
